/**
 * Motor de diagnóstico executivo DataFlow — gestão por exceção.
 * Interpretação frontend; não altera regras de negócio.
 */

import { formatExceptionCount, shouldShowCount, type TplSeverity } from './tplSeverityStyles';

export type DataflowExecutiveTone = TplSeverity;

export interface DataflowExecutiveInput {
  ielScore: number;
  maturityScore: number;
  disciplinaPct: number;
  riskCount: number;
  lossCount: number;
  actionCount: number;
  escalationsOverdue?: number;
  plannerConflicts?: number;
  criticalLossCount?: number;
}

export interface DataflowExecutiveStatus {
  tone: DataflowExecutiveTone;
  label: string;
  reasons: string[];
}

export interface DataflowExecutiveRibbonData {
  iel: number;
  maturidade: number;
  riscos: number;
  perdas: number;
  acoes: number;
  status: DataflowExecutiveStatus;
}

const CRITICAL_LOSS_LIMIT = 3;

export function computeDataflowExecutiveStatus(input: DataflowExecutiveInput): DataflowExecutiveStatus {
  const reasons: string[] = [];
  const escalations = input.escalationsOverdue ?? 0;
  const conflicts = input.plannerConflicts ?? 0;
  const criticalLosses = input.criticalLossCount ?? 0;

  if (input.disciplinaPct < 70) {
    reasons.push(`Disciplina ${input.disciplinaPct}%`);
  }
  if (input.ielScore < 60) {
    reasons.push(`IEL ${input.ielScore}/100`);
  }
  if (shouldShowCount(escalations)) {
    reasons.push(formatExceptionCount(escalations, 'escalonamento vencido', 'escalonamentos vencidos')!);
  }
  if (criticalLosses >= CRITICAL_LOSS_LIMIT) {
    reasons.push(formatExceptionCount(criticalLosses, 'perda crítica', 'perdas críticas')!);
  }
  if (shouldShowCount(conflicts)) {
    reasons.push(formatExceptionCount(conflicts, 'conflito de agenda', 'conflitos de agenda')!);
  }
  if (shouldShowCount(input.riskCount) && input.riskCount >= 3) {
    reasons.push(`${input.riskCount} riscos ativos`);
  }

  const isCritico =
    input.ielScore < 60 ||
    input.disciplinaPct < 70 ||
    escalations > 0 ||
    criticalLosses >= CRITICAL_LOSS_LIMIT;

  if (isCritico) {
    return { tone: 'critico', label: 'CRÍTICO', reasons };
  }

  const isAtencao =
    input.ielScore < 80 ||
    input.disciplinaPct < 85 ||
    input.riskCount > 0 ||
    input.lossCount > 0 ||
    input.actionCount > 0;

  if (isAtencao) {
    if (reasons.length === 0 && input.ielScore < 80) {
      reasons.push(`IEL em ${input.ielScore}/100 — abaixo da meta`);
    }
    return { tone: 'atencao', label: 'ATENÇÃO', reasons };
  }

  return { tone: 'normal', label: 'NORMAL', reasons: [] };
}

export function buildDataflowExecutiveRibbon(input: DataflowExecutiveInput): DataflowExecutiveRibbonData {
  return {
    iel: input.ielScore,
    maturidade: input.maturityScore,
    riscos: input.riskCount,
    perdas: input.lossCount,
    acoes: input.actionCount,
    status: computeDataflowExecutiveStatus(input),
  };
}

/** Extrai número de valores como "50/100" ou "67%". */
export function parseMetricValue(raw: string): number {
  const m = raw.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}
